/* eslint-disable @angular-eslint/no-outputs-metadata-property */
/* eslint-disable @angular-eslint/no-inputs-metadata-property */
import { Component, Injector, OnInit, inject, ChangeDetectionStrategy, input, output, viewChild } from '@angular/core';
import { FormControl, FormsModule, NG_VALUE_ACCESSOR, ReactiveFormsModule } from '@angular/forms';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { NEVER, merge } from 'rxjs';

import { CommonModule } from '@angular/common';
import { SelectChangeEvent } from '@openng/optimus-ui/select';
import { MultiSelectModule } from '@openng/optimus-ui/multiselect';
import { ControlValueAccessorConnectorComponent } from '../../control-value-accessor.connector';
import { BaseFormControlDirective, BaseFormControlDirectiveInputs } from '../../directives/base-form-control.directive';
import {
  DropdownFormControlDirective,
  DropdownFormControlDirectiveInputs,
  DropdownFormControlDirectiveOutputs,
} from '../../directives/dropdown-form-control.directive';
import {
  SelectFormControlDirective,
  SelectFormControlDirectiveInputs,
} from '../../directives/select-form-control.directive';
import { FormErrorComponent } from '../../form-error/form-error.component';

import { FormFieldUniqNames } from '../../form-fields-uniq-names.service';
import { FormComponent } from '../../form/form.component';
import { FormConnectorService } from '../../forms.utils';
import { InputWrapperComponent } from '../../input-wrapper/input-wrapper.component';
import { SelectComponent } from '../select.component';
import { uniqueId } from '@shared/utils';

export enum DropdownState {
  Closed = 'closed',
  Opened = 'opened',
}

@Component({
  selector: 'ui-multiselect-checkbox',
  templateUrl: './multiselect-checkbox.component.html',
  host: { class: 'block mb-4' },
  imports: [CommonModule, ReactiveFormsModule, FormsModule, MultiSelectModule, InputWrapperComponent],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: MultiSelectCheckboxComponent,
      multi: true,
    },
  ],
  changeDetection: ChangeDetectionStrategy.Eager,
  hostDirectives: [
    {
      directive: BaseFormControlDirective,
      inputs: BaseFormControlDirectiveInputs,
    },
    {
      directive: SelectFormControlDirective,
      inputs: SelectFormControlDirectiveInputs,
    },
    {
      directive: DropdownFormControlDirective,
      inputs: DropdownFormControlDirectiveInputs,
      outputs: DropdownFormControlDirectiveOutputs,
    },
  ],
})
export class MultiSelectCheckboxComponent extends ControlValueAccessorConnectorComponent {
  private uniqNames = inject(FormFieldUniqNames);
  private _form = inject(FormComponent, { optional: true, skipSelf: true });
  protected _formConnectorService = inject(FormConnectorService, { optional: true, skipSelf: true });
  readonly handleChange = output<SelectChangeEvent>();
  /** Vysvětlení zakázané položky při najetí myší, pokud položka nemá vlastní (IOption.disabledHint). */
  readonly disabledItemHint = input('Položka není povolena k vybrání.');

  readonly wrapper = viewChild.required(InputWrapperComponent);

  submitted = false;
  internalName: string;
  dropdownState = DropdownState;
  randomInputId = uniqueId();
  // Unikátní class overlay panelu – umožní oscopovat DOM dotaz jen na tento multiselect
  // (panel je appendTo="body", jinak by document.querySelector sáhl na cizí panel).
  readonly panelStyleClass = `ui-ms-panel-${this.randomInputId}`;

  base = inject(BaseFormControlDirective);
  select = inject(SelectFormControlDirective);
  dropdown = inject(DropdownFormControlDirective);

  constructor() {
    const injector = inject(Injector);

    super(injector, { change: false });
    this.internalName = this.uniqNames.getSelectName();

    merge<boolean[]>(this._formConnectorService?.submitted$ ?? NEVER, this._form?.getSubmitted() ?? NEVER)
      .pipe(takeUntilDestroyed())
      .subscribe((_) => (this.submitted = _));
  }

  onPanelShowHandler() {
    this.dropdown.dropdownStateChange.emit(this.dropdownState.Opened);
    this.addTitleAndLabelToSelectAll();
  }

  clear() {
    this.getFormControl.setValue(null);
  }

  callChangeDetection() {
    this.wrapper().callChangeDetection();
  }

  addTitleAndLabelToSelectAll() {
    setTimeout(() => {
      // Scope jen na vlastní panel (viz panelStyleClass) – jinak by při otevření dalšího dropdownu,
      // dokud předchozí panel ještě dozavírá, dotaz trefil cizí panel a nový zůstal bez „Vybrat vše".
      const selectAllCheckbox = document.querySelector(`.${this.panelStyleClass} .p-multiselect-header .p-checkbox`);
      if (selectAllCheckbox) {
        selectAllCheckbox.setAttribute('title', 'Vybrat vše');

        const label = document.createElement('label');
        label.textContent = this.dropdown.selectAllLabel();
        label.style.cursor = 'pointer';

        const wrapper = document.createElement('div');
        wrapper.style.display = 'inline-flex';
        wrapper.style.alignItems = 'center';
        wrapper.style.cursor = 'pointer';

        selectAllCheckbox.parentNode?.insertBefore(wrapper, selectAllCheckbox);
        wrapper.appendChild(selectAllCheckbox);
        wrapper.appendChild(label);

        wrapper.addEventListener('click', () => {
          (selectAllCheckbox as HTMLElement).click();
        });
      }
    }, 0);
  }
}

/** This component holds the value as comma separated string of ids */
@Component({
  selector: 'ui-multiselect-checkbox-string',
  templateUrl: './multiselect-checkbox-string.component.html',
  imports: [
    CommonModule,
    ReactiveFormsModule,
    FormsModule,
    FormErrorComponent,
    MultiSelectModule,
    InputWrapperComponent,
  ],
  host: { class: 'block mb-4' },
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: MultiSelectCheckboxStringComponent,
      multi: true,
    },
  ],
  changeDetection: ChangeDetectionStrategy.Eager,
  hostDirectives: [
    {
      directive: BaseFormControlDirective,
      inputs: BaseFormControlDirectiveInputs,
    },
    {
      directive: SelectFormControlDirective,
      inputs: SelectFormControlDirectiveInputs,
    },
    {
      directive: DropdownFormControlDirective,
      inputs: DropdownFormControlDirectiveInputs,
      outputs: DropdownFormControlDirectiveOutputs,
    },
  ],
})
export class MultiSelectCheckboxStringComponent extends SelectComponent implements OnInit {
  fc = new FormControl<number[]>([]);

  constructor() {
    super();

    this.fc.valueChanges.pipe(takeUntilDestroyed()).subscribe((v) => {
      const val = (v as number[]) ?? [];
      this.getFormControl?.setValue(val.join(','));
      this.getFormControl.markAsDirty();
    });
  }

  ngOnInit() {
    this.fc.setValue(((this.getFormControl?.value as string) ?? '').split(',').map((a) => parseInt(a)));
  }

  clear() {
    this.fc.setValue(null);
    this.getFormControl.setValue(null);
    this.getFormControl.markAsDirty();
  }
}
